import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  OnInit,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../../molecules/button/button.component';
import { ButtonType } from '../../../../shared/enums/button-type.enums';
import { IconComponent } from '../../../atoms/icon/icon.component';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { LinkComponent } from '../../../atoms/link/link.component';
import LinkType from '../../../../shared/enums/link-type.enums';
import { Colors, Sizes } from '../../../../shared/enums/shared.enums';
import {
  EventItem,
  Paragraph,
} from '../../../../shared/interfaces/termine-item.interface';
import { HrefType } from '../../../../shared/enums/href-type.enums';
import ViewportType from '../../../../shared/enums/viewport-type.enums';
import {
  CaasComponents,
  CaasLayout,
} from '../../../../shared/interfaces/caas/caas.interface';
import { CaasBodytextService } from '../../../../services/caas-bodytext.service';
import { ShowMoreLessComponent } from '../../show-more-less/show-more-less.component';
import { ActivatedRoute } from '@angular/router';
import { CapitalizePipe } from '../../../capitalize.pipe';

@Component({
  selector: 'hdi-termine',
  standalone: true,
  templateUrl: './termine.component.html',
  styleUrls: ['./termine.component.scss'],
  imports: [
    CommonModule,
    ButtonComponent,
    IconComponent,
    LinkComponent,
    ShowMoreLessComponent,
    CapitalizePipe,
  ],
  animations: [
    trigger('rotateAnimation', [
      state('default', style({ transform: 'rotate(0deg)' })),
      state('open', style({ transform: 'rotate(90deg)' })),
      state('clicked', style({ transform: 'rotate(90deg)' })),
      transition('default <=> open', animate('250ms ease-in-out')),
      transition('default <=> clicked', animate('250ms ease-out')),
    ]),
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class TermineComponent implements OnInit, OnChanges {
  @Input() caasData?: CaasComponents['value'][number];
  @Input() eventsData?: CaasLayout[];
  @Input() headerLabel: string = 'Termine';
  @Input() viewport: ViewportType = ViewportType.desktop;
  @Input() eventItems!: EventItem[];
  @Input() title: string | undefined = '';
  @Input() labelCalendar: string = 'Weiterbildungstermine';
  @Input() bgColor?: Colors;
  @Input() expandState?: string;
  @Input() isSectionBg?: boolean;

  @ViewChild('event_content', { static: true }) AccordionToggle!: ElementRef;

  LinkType = LinkType;
  color = Colors;
  size = Sizes;
  HrefType = HrefType;

  isOpen: boolean[] = [];
  animationStates: string[] = [];
  accordion: ButtonType = ButtonType.OPEN_CLOSE_SECONDARY;
  events: CaasLayout[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private caasBodytextService: CaasBodytextService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.updateAnimationStates();
    this.handleFragment();
  }

  private handleFragment(): void {
    this.route.fragment.subscribe((fragment) => {
      if (fragment) {
        this.openAccordionById(fragment);
      }
    });
  }

  private openAccordionById(id: string): void {
    const index = this.eventItems.findIndex((event) => event.id === id);
    if (index !== -1) {
      this.isOpen.fill(false);
      this.isOpen[index] = true;
      this.updateAnimationStates();

      // Scroll to the accordion after it's opened
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const topOffset =
            element.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: topOffset, behavior: 'smooth' });
        }
      }, 100);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData'] && this.caasData) {
      if (Array.isArray(this.caasData) && this.caasData.length) {
        this.setCaasContent(this.caasData[0]);
      }
      if (this.eventsData && this.eventsData.length) {
        this.setCaasEvents(this.caasData);
        this.setCaasContent(this.caasData);
        this.sortEventsByDate();
      }
    }
    this.cdr.detectChanges();
    // console.log('events', this.events, this.eventItems);
  }

  private updateAnimationStates() {
    this.animationStates = this.isOpen.map((isOpen) =>
      isOpen ? 'open' : 'default'
    );
  }

  toggleAnimation(index: number) {
    this.animationStates[index] =
      this.animationStates[index] === 'default' ? 'clicked' : 'default';
  }

  toggleOpenClose(index: number) {
    setTimeout(() => {
      if (this.isOpen[index]) {
        // Delay the final closure to allow the closing animation to play
        this.isOpen[index] = false;
        this.updateAnimationStates();
        this.cdr.detectChanges();
      } else {
        this.isOpen.fill(false);
        this.isOpen[index] = true;
        this.updateAnimationStates();
        this.cdr.detectChanges();
      }
    }, 250);
  }

  setCaasIsOpen(caasData: CaasComponents['value'][number]) {
    this.isOpen = new Array(this.eventsData?.length ?? 0).fill(false);
    if (caasData?.formData?.st_expand?.value?.identifier === 'firstOpen') {
      this.isOpen[0] = true;
    }
    if (caasData?.formData?.st_expand?.value?.identifier === 'allOpen') {
      this.isOpen.fill(true);
    }
  }

  extractEventData(data: CaasLayout): EventItem {
    try {
      const formData = data?.['formData'];
      if (!formData) {
        throw new Error(
          'Termine: extractEventData formData is null or undefined'
        );
      }

      const eventDate = formData.tt_date?.value
        ? new Date(formData.tt_date.value as string)
        : null;
      return {
        id: formData.tt_anchor_name.value || '',
        time: eventDate ? eventDate.toLocaleTimeString() : '',
        headline: formData.tt_header?.value ?? '',
        preheadline: formData.tt_preheader?.value ?? '',
        day: eventDate ? eventDate.getDate().toString() : '',
        month: eventDate
          ? eventDate
              .toLocaleString('default', { month: 'short' })
              .toUpperCase()
          : '-',
        year: eventDate ? eventDate.getFullYear().toString() : '-',
        timestamp: eventDate ? eventDate.getTime() : 0,
        content: this.parseContent(formData.tt_contents?.value ?? ''),
        speakers: formData.tt_speakers?.value ?? '',
        educationTime: formData.tt_eligible_time?.value ?? '',
        key: data?.identifier ?? '',
        link: {
          url:
            formData.tt_application_link.value.formData.lt_link_external
              .value ||
            formData.tt_application_link.value.formData.lt_link_internal.value?.uid?.toString() ||
            '',
          type: formData.tt_application_link?.value?.formData?.lt_link_external
            ?.value
            ? LinkType.EXTERNAL
            : LinkType.INLINE,
          label:
            formData.tt_application_link?.value?.formData?.lt_link_label
              ?.value ?? '',
          hrefType: formData.tt_application_link?.value?.formData
            ?.lt_link_external?.value
            ? HrefType.EXTERNAL
            : HrefType.ANGULAR_ROUTE,
          follow:
            formData.tt_application_link?.value?.formData?.lt_link_follow
              ?.value,
          target:
            formData.tt_application_link?.value?.formData?.lt_link_target?.value?.identifier.toString() ===
            'new'
              ? '_blank'
              : '_self',
          title:
            formData.tt_application_link?.value?.formData?.lt_link_title
              ?.value ?? '',
        },
        start_time: formData.tt_starttime?.value ?? '',
        end_time: formData.tt_endtime?.value ?? '',
      };
    } catch (error) {
      console.warn('Termine: Error extracting event data:', error, data);
      return {} as EventItem;
    }
  }

  parseContent(htmlContent: string): Paragraph[] {
    try {
      const formattedContent =
        this.caasBodytextService.extractTextEditorContent(htmlContent) || '';
      return [{ text: formattedContent }];
    } catch (error) {
      console.warn('Termine: Error parsing content:', error);
      return [{ text: '' }];
    }
  }

  setCaasContent(caasData: CaasComponents['value'][number]) {
    this.title = caasData?.formData?.st_header?.value ?? 'Termine';
  }

  setCaasEvents(caasData: CaasComponents['value'][number]) {
    if (!Array.isArray(this.eventsData) || this.eventsData.length === 0) {
      console.warn('Termine: No events data available to process.');
      return;
    }

    this.eventItems = this.eventsData.map((event) =>
      this.extractEventData(event)
    );
    this.setCaasIsOpen(caasData);
  }

  formatDayTime(event: EventItem): string {
    // Safely retrieve locale or use default
    const locale = this.caasData?.formData?.locale?.identifier || 'de-DE';

    try {
      const formatter = new Intl.DateTimeFormat(locale, { month: 'short' });

      // Generate month mapping dynamically
      const months = Array.from({ length: 12 }, (_, index) =>
        formatter.format(new Date(2024, index, 1)).toUpperCase()
      );

      // Parse month and day
      const monthIndex = months.indexOf(event.month.toUpperCase());
      const day = Number(event.day);

      if (monthIndex === -1 || isNaN(day)) {
        console.warn('Invalid date components:', event);
        return '-';
      }

      // Create and validate date object
      const eventDate = new Date(new Date().getFullYear(), monthIndex, day);
      if (isNaN(eventDate.getTime())) {
        console.warn('Invalid date:', event);
        return '-';
      }

      // Format time details
      const formattedTime = event.start_time
        ? `${this.formatTime(event.start_time)}${
            event.end_time ? ` - ${this.formatTime(event.end_time)}` : ''
          } Uhr`
        : 'Uhrzeit folgt noch';

      // Format full output
      const weekday = eventDate.toLocaleDateString(locale, { weekday: 'long' });
      return `${weekday}, ${formattedTime} ${event.preheadline || ''}`.trim();
    } catch (error) {
      console.error('Error formatting date/time:', error, event);
      return '-';
    }
  }

  formatTime(isoDateString: string): string {
    try {
      const date = new Date(isoDateString);
      if (isNaN(date.getTime()))
        throw new Error(`Invalid time format: ${isoDateString}`);
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      return `${hours}.${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      console.warn('Error formatting time:', error);
      return '-';
    }
  }

  sortEventsByDate() {
    this.eventItems.sort((a, b) => {
      return a.timestamp - b.timestamp;
    });
  }
}
